import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import Header from './components/Header/Header';
import Dashboard from './components/Dashboard/Dashboard';
import WriteModal from './components/WriteModal/WriteModal';
import InsightsPage from './components/InsightsPage/InsightsPage';
import SettingsPage from './components/SettingsPage/SettingsPage';
import ProfilePage from './components/ProfilePage/ProfilePage';
import AuthModal from './components/AuthModal/AuthModal';
import DiaryModal from './components/DiaryModal/DiaryModal';
import AboutPage from './components/AboutPage/AboutPage';
import PrivacyPage from './components/PrivacyPage/PrivacyPage';
import TermsPage from './components/TermsPage/TermsPage';
import api, { formatEntriesForDisplay } from './services/api';
import './App.css';

const PUBLIC_PATHS = ['/about', '/privacy', '/terms'];

// Wrapper component for edit entry route
function WriteEntryWrapper({ entries, onSave, entriesCount }) {
  const { entryId } = useParams();
  const editingEntry = entries.find(e => e.id === entryId || e.entryId === entryId);
  
  return <WriteModal onSave={onSave} editingEntry={editingEntry} entriesCount={entriesCount} />;
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [activeModal, setActiveModal] = useState(null);
  const [entries, setEntries] = useState([]);
  const [user, setUser] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check authentication and load data on mount
  useEffect(() => {
    const initializeApp = async () => {
      if (api.isAuthenticated()) {
        try {
          // Fetch current user
          const userData = await api.auth.getCurrentUser();
          setUser(userData.user);
          setIsAuthenticated(true);
          
          // Fetch entries
          await loadEntries();
        } catch (err) {
          console.error('Failed to initialize app:', err);
          // If token is invalid, logout
          if (err.status === 401 || err.status === 403) {
            handleLogout();
          }
        }
      }
      setLoading(false);
    };

    initializeApp();
  }, []);

  // Load entries from API (initial load - 50 entries)
  const loadEntries = async () => {
    try {
      const data = await api.entries.getEntries({ limit: 50, sort: '-timestamp' });
      const formattedEntries = formatEntriesForDisplay(data.entries);
      setEntries(formattedEntries);
    } catch (err) {
      console.error('Failed to load entries:', err);
    }
  };

  const handleAuth = async (email, password, mode, name = null) => {
    try {
      let response;
      
      if (mode === 'signup') {
        response = await api.auth.register(name || email.split('@')[0], email, password);
      } else {
        response = await api.auth.login(email, password);
      }
      
      setUser(response.user);
      setIsAuthenticated(true);
      setActiveModal(null);
      
      // Load entries after authentication
      await loadEntries();
    } catch (err) {
      console.error('Authentication failed:', err);
      throw new Error(err.message || 'Authentication failed');
    }
  };

  const handleLogout = () => {
    api.auth.logout();
    setIsAuthenticated(false);
    setUser(null);
    setEntries([]);
    setActiveModal(null);
  };

  // Check if entry exists for today
  const getTodayEntry = () => {
    const today = new Date();
    const todayDateStr = today.toLocaleDateString('en-US', { 
      year: 'numeric',
      month: '2-digit', 
      day: '2-digit'
    });
    
    return entries.find(entry => {
      const entryDate = new Date(entry.timestamp);
      const entryDateStr = entryDate.toLocaleDateString('en-US', { 
        year: 'numeric',
        month: '2-digit', 
        day: '2-digit'
      });
      return entryDateStr === todayDateStr;
    });
  };

  const handleEditEntry = (entry) => {
    // Navigate to write page with entry ID
    const entryId = entry.id || entry.entryId;
    navigate(`/write/${entryId}`);
  };

  const handleSaveEntry = async (entry, editingEntryId = null) => {
    try {
      if (editingEntryId) {
        // Update existing entry - find the entry from entries array
        const existingEntry = entries.find(e => e.id === editingEntryId || e.entryId === editingEntryId);
        if (!existingEntry) {
          throw new Error('Entry not found');
        }
        
        const entryId = existingEntry.entryId || existingEntry.id;
        const updatedData = await api.entries.updateEntry(entryId, {
          text: entry.text,
          mood: entry.mood,
        });
        
        // Update entry in state
        setEntries(entries.map(e => 
          (e.id === editingEntryId || e.entryId === editingEntryId)
            ? formatEntriesForDisplay([updatedData.entry])[0]
            : e
        ));
      } else {
        // Create new entry
        const newEntryData = await api.entries.createEntry({
          text: entry.text || '',
          mood: entry.mood,
          timestamp: new Date().toISOString(),
        });
        
        const formattedEntry = formatEntriesForDisplay([newEntryData.entry])[0];
        setEntries([formattedEntry, ...entries]);
      }
      
      setEditingEntry(null);
      setActiveModal(null);
    } catch (err) {
      console.error('Failed to save entry:', err);
      throw err; // Re-throw to handle in WriteModal if needed
    }
  };

  const handleDeleteEntry = async (id) => {
    try {
      // Find the entry to get the correct entryId
      const entry = entries.find(e => e.id === id || e.entryId === id);
      const entryId = entry.entryId || entry.id;
      
      await api.entries.deleteEntry(entryId);
      
      // Remove from state
      setEntries(entries.filter(e => e.id !== id && e.entryId !== id));
    } catch (err) {
      console.error('Failed to delete entry:', err);
      throw err;
    }
  };

  const handleUpdateUser = async (updatedUserData) => {
    try {
      // If updatedUserData is already a user object (from ProfilePage after API call),
      // just update the state
      if (updatedUserData.userId || updatedUserData.email) {
        setUser(updatedUserData);
      } else {
        // Otherwise, call the API (for backward compatibility)
        const response = await api.user.updateProfile(updatedUserData);
        setUser(response.user);
      }
    } catch (err) {
      console.error('Failed to update user:', err);
      throw err;
    }
  };

  // Public pages are accessible without authentication
  if (PUBLIC_PATHS.includes(location.pathname)) {
    return (
      <div className="app">
        <div className="app__background">
          <div className="app__gradient-orb app__gradient-orb--1"></div>
          <div className="app__gradient-orb app__gradient-orb--2"></div>
          <div className="app__gradient-orb app__gradient-orb--3"></div>
        </div>
        <Routes>
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
        </Routes>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="app">
        <div className="app__background">
          <div className="app__gradient-orb app__gradient-orb--1"></div>
          <div className="app__gradient-orb app__gradient-orb--2"></div>
          <div className="app__gradient-orb app__gradient-orb--3"></div>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          color: 'white',
          fontSize: '1.2rem',
          fontWeight: 500,
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="app">
        <div className="app__background">
          <div className="app__gradient-orb app__gradient-orb--1"></div>
          <div className="app__gradient-orb app__gradient-orb--2"></div>
          <div className="app__gradient-orb app__gradient-orb--3"></div>
        </div>
        <AuthModal 
          mode={authMode}
          onAuth={handleAuth}
          onSwitchMode={() => setAuthMode(authMode === 'login' ? 'signup' : 'login')}
        />
      </div>
    );
  }

  return (
    <div className="app">
        <div className="app__background">
          <div className="app__gradient-orb app__gradient-orb--1"></div>
          <div className="app__gradient-orb app__gradient-orb--2"></div>
          <div className="app__gradient-orb app__gradient-orb--3"></div>
        </div>

        <Routes>
          {/* Home/Dashboard Route */}
          <Route 
            path="/" 
            element={
              <>
                {entries.length > 0 && (
                  <Header 
                    onOpenModal={setActiveModal}
                    onLogout={handleLogout}
                    user={user}
                  />
                )}
                <Dashboard 
                  entries={entries}
                  onDeleteEntry={handleDeleteEntry}
                  onOpenWrite={() => {
                    const todayEntry = getTodayEntry();
                    if (todayEntry) {
                      navigate(`/write/${todayEntry.id || todayEntry.entryId}`);
                    } else {
                      navigate('/write');
                    }
                  }}
                  onEditEntry={handleEditEntry}
                  onOpenModal={setActiveModal}
                  user={user}
                  todayEntry={getTodayEntry()}
                />
              </>
            } 
          />

          {/* Insights Page Route */}
          <Route 
            path="/insights" 
            element={
              <>
                {entries.length > 0 && (
                  <Header 
                    onOpenModal={setActiveModal}
                    onLogout={handleLogout}
                    user={user}
                  />
                )}
                <InsightsPage />
              </>
            } 
          />

          {/* Settings Page Route */}
          <Route 
            path="/settings" 
            element={
              <>
                {entries.length > 0 && (
                  <Header 
                    onOpenModal={setActiveModal}
                    onLogout={handleLogout}
                    user={user}
                  />
                )}
                <SettingsPage onDataCleared={loadEntries} />
              </>
            } 
          />

          {/* Profile Page Route */}
          <Route 
            path="/profile" 
            element={
              <>
                {entries.length > 0 && (
                  <Header 
                    onOpenModal={setActiveModal}
                    onLogout={handleLogout}
                    user={user}
                  />
                )}
                <ProfilePage 
                  user={user}
                  onLogout={handleLogout}
                  onUpdateUser={handleUpdateUser}
                />
              </>
            } 
          />

          {/* Diary Page Route */}
          <Route 
            path="/diary" 
            element={
              <DiaryModal entries={entries} />
            } 
          />

          {/* Write Page Route - New Entry */}
          <Route 
            path="/write" 
            element={
              <WriteModal 
                onSave={handleSaveEntry}
                editingEntry={null}
                entriesCount={entries.length}
              />
            } 
          />

          {/* Write Page Route - Edit Entry */}
          <Route 
            path="/write/:entryId" 
            element={
              <WriteEntryWrapper 
                entries={entries}
                onSave={handleSaveEntry}
                entriesCount={entries.length}
              />
            } 
          />

          {/* Public pages — accessible when authenticated too */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* Redirect unknown routes to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* Modals - kept for backward compatibility if needed */}
        {activeModal === 'write' && (
          <WriteModal 
            onClose={() => {
              setEditingEntry(null);
              setActiveModal(null);
            }}
            onSave={handleSaveEntry}
            editingEntry={editingEntry}
          />
        )}

      </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
